/// <amd-module name="VSS/Features/Charts/Internal/TabularComponent" />
import React = require("react");
import { format } from "VSS/Core/Util/String";
import * as Charts_Resources from "VSS/Features/Charts/Resources";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { ChartProps, ChartTypesConstants, Datum, LabelFormatModes } from "../Contracts";
import { TimeSpanFormatter } from "./AxisLabelFormatter";

import "VSS/LoaderPlugins/Css!Charts/Chart";

/** Responsible for rendering a screen-readable variant of chart content.
 * This was converted from JQuery implementation in Vssf WebPlatform Charts.  */
export class TabularComponent extends VssComponent<ChartProps, {}> {
    constructor(props: ChartProps, context?: IVssComponentContext) {
        super(props, context);
    }

    public render(): JSX.Element {
        /*Refer to the following docs for basics on accessible tabular rendering.
            http://webaim.org/techniques/tables/data
            http://webaim.org/blog/jaws-ate-my-tables/
            For background, guidance & best practices on creating accessible tables for screen readers.
            Quick note: scope attributes are critical for describing header elements.
        */
        if (!this.props.showAccessibleForm) {
            return null;
        }

        const showLabels = this.props.xAxis != null && this.props.xAxis.labelValues != null && this.props.xAxis.labelsEnabled;

        // Render Header Row
        let maxLength = 0; // The length of the longest series in the data set, used for determining max bounds of main loop.

        const thElements: JSX.Element[] = [];
        if (showLabels) {
            maxLength = this.props.xAxis.labelValues.length;
            const colHeaderText = this.interpretAxisHeader(this.props);
            thElements.push(
                <th scope="col" key={"xAxisHeader"}>
                    {colHeaderText}
                </th>
            );
        }

        for (let i = 0; i < this.props.series.length; i++) {
            const valueText = this.interpretSeriesHeader(this.props, i);
            thElements.push(
                <th scope="col" key={i}>
                    {valueText}
                </th>
            );

            const length = this.props.series[i].data.length;
            maxLength = maxLength > length ? maxLength : length;
        }

        const hasSparseTrendFormat: boolean = this.hasSparseTrendFormat(this.props);

        // Render Body Section
        const bodyRows: JSX.Element[] = [];
        for (let j = 0; j < maxLength; j++) {
            const rowElements = [];
            let rowLabelText;
            if (showLabels) {
                rowLabelText = this.interpretLabel(this.props, j);
                rowElements.push(
                    <th scope="row" key={"xAxisLabel"}>
                        {rowLabelText}
                    </th>
                );
            }

            for (let i = 0; i < this.props.series.length; i++) {
                let valueText;
                if (hasSparseTrendFormat) {
                    valueText = this.getValueTextForSparseTrendFormat(this.props, i, rowLabelText);
                } else {
                    const value = this.props.series[i].data[j];
                    if (value == null) {
                        valueText = "";
                    } else if (Array.isArray(value)) {
                        valueText = value.toLocaleString();
                    } else if (typeof value == "object") {
                        valueText = (value as Datum).y;
                    } else {
                        valueText = value;
                    }
                }

                rowElements.push(
                    <td scope="row" key={i}>
                        {valueText}
                    </td>
                );
            }
            bodyRows.push(<tr key={j}>{rowElements}</tr>);
        }

        return this.renderTable(thElements, bodyRows);
    }

    public interpretAxisHeader(chartOptions: ChartProps): string {
        if (chartOptions.xAxis.title) {
            return chartOptions.xAxis.title;
        } else if (chartOptions.xAxis.labelFormatMode == LabelFormatModes.DateTime_DayInMonth) {
            return Charts_Resources.HtmlTable_DateLabel;
        } else if (chartOptions.xAxis.labelFormatMode == LabelFormatModes.NumberSeconds_HourMinuteSeconds) {
            return Charts_Resources.HtmlTable_DurationLabel;
        } else {
            return Charts_Resources.HtmlTable_SampleLabel;
        }
    }

    /**
     * Interprets the column header at the specified index position
     * @param chartOptions Must include series array. Caller implementation is responsible for ensuring this.
     * @param index
     */
    public interpretSeriesHeader(chartOptions: ChartProps, index: number): string {
        const name = chartOptions.series[index].name;
        return name != null ? name : format(Charts_Resources.HtmlTable_SeriesLabel, index);
    }

    /**
     * Interprets the row label at the specified index position
     * @param chartOptions Must include xAxis with labelValues . Caller is responsible for ensuring this.
     * @param index
     */
    public interpretLabel(chartOptions: ChartProps, index: number): string {
        const axis = chartOptions.xAxis;
        const value = axis.labelValues[index];
        if (value != null && axis.labelFormatMode == LabelFormatModes.DateTime_DayInMonth) {
            const date = new Date(chartOptions.xAxis.labelValues[index]);
            return this.formatShortDate(date);
        } else if (value != null && axis.labelFormatMode == LabelFormatModes.NumberSeconds_HourMinuteSeconds) {
            return TimeSpanFormatter.convertToTimeFormat(chartOptions.xAxis.labelValues[index]);
        } else {
            return value;
        }
    }

    /**
     * Interprets the given X value
     * @param labelFormatMode Label format mode. Caller is responsible for ensuring this.
     * @param value X value to format
     */
    public interpretXValue(labelFormatMode: LabelFormatModes, value: any): string {
        if (value != null && labelFormatMode == LabelFormatModes.DateTime_DayInMonth) {
            const date = new Date(value);
            return this.formatShortDate(date);
        } else if (value != null && labelFormatMode == LabelFormatModes.NumberSeconds_HourMinuteSeconds) {
            return TimeSpanFormatter.convertToTimeFormat(value);
        } else {
            return value;
        }
    }

    protected renderTable(thElements: JSX.Element[], bodyRows: JSX.Element[]) {
        return (
            <table>
                <thead>
                    <tr>{thElements}</tr>
                </thead>
                <tbody>{bodyRows}</tbody>
            </table>
        );
    }

    /**
     * Only Returns TRUE if the chart is a trend chart with line rendering, with each series defined in format of an array of 2 element [x,y] points
     * Otherwise returns FALSE
     * @param chartOptions
     */
    private hasSparseTrendFormat(chartOptions: ChartProps): boolean {
        if (
            !chartOptions.xAxis ||
            chartOptions.chartType !== ChartTypesConstants.Line ||
            (chartOptions.xAxis.labelFormatMode !== LabelFormatModes.DateTime_DayInMonth &&
                chartOptions.xAxis.labelFormatMode !== LabelFormatModes.NumberSeconds_HourMinuteSeconds)
        ) {
            return false;
        }

        for (let i = 0; i < chartOptions.series.length; i++) {
            for (let j = 0; j < chartOptions.series[i].data.length; j++) {
                const value = chartOptions.series[i].data[j];
                // currently we are handling only the case of point defined by [x,y] pair
                if (Array.isArray(value) && value.length === 2) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Get the value associated with the current row label
     * @param chartOptions
     * @param index
     * @param rowLabelText
     */
    private getValueTextForSparseTrendFormat(chartOptions: ChartProps, seriesIndex: number, rowLabelText: string): string {
        // default y value
        let valueText = "";

        // find the right y value of the point where x value matches the current rowLabelText
        const currentSeries = chartOptions.series[seriesIndex];
        for (let m = 0; m < currentSeries.data.length; m++) {
            const value = currentSeries.data[m];
            const xValueFormatted = this.interpretXValue(chartOptions.xAxis.labelFormatMode, value[0]);
            if (xValueFormatted === rowLabelText) {
                valueText = value[1] === undefined ? "" : value[1];
                break;
            }
        }

        return valueText;
    }

    private formatShortDate(date: Date) {
        return date.toLocaleDateString(navigator.language);
    }
}
